import { getCountryData, TCountryCode } from 'countries-list';
import { NoteFormState } from '@/pages/note-generator/types/note-form.type';
import { getAverateAmount } from '@/pages/note-generator/utils/calc-avg-amount.utils.';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getStampRate } from '@/pages/mortgage-workflow/utils/calc-stampduty.utils';

const initialState: NoteFormState = {
  lvr: 0,
  cashOutReason: '',
  propertyPurpose: '',
  propertyPurposeMultiple: [],
  citizenship: '',
  residency: '',
  incomeSource: '',
  occupation: '',
  baseSalary: 0,
  salaryFrequency: '',
  averageSalary: 0,
  averageSalaryAud: 0,
  salaryDeduction: 0,
  kidsAge: [],
  salaryCurrencyTo: 'AUD',
  thisYearAnnualCashBonus: 0,
  lastYearAnnualCashBonus: 0,
  coapplicantSalaryDeduction: 0,
  ausProperties: [],
  offset: false,
  ausMortgageInterestType1: '',
  repaymentType: '',
  repaymentFrequency: '',
  loanStructure: '',
  googleSheetId: null,
  assessmentFolderId: null,
  dealId: null,
  contactId: null,
};

const noteFormSlice = createSlice({
  name: 'noteForm',
  initialState,
  reducers: {
    setKidsAge: (state, action: PayloadAction<number[]>) => {
      state.kidsAge = action.payload;
    },
    setPropertyPurposeMultiple: (state, action: PayloadAction<string[]>) => {
      state.propertyPurposeMultiple = action.payload;
    },
    setNoteFromField: (
      state,
      action: PayloadAction<{ field: keyof NoteFormState; value: any }>,
    ) => {
      const { field, value } = action.payload;
      state[field] = value;

      // calculate average salary
      if (field === 'baseSalary' || field === 'salaryFrequency') {
        const updatedBaseSalary =
          field === 'baseSalary' ? value : state.baseSalary;
        const updatedSalaryFrequency =
          field === 'salaryFrequency' ? value : state.salaryFrequency;
        if (updatedBaseSalary && updatedSalaryFrequency) {
          state.averageSalary = getAverateAmount(
            updatedSalaryFrequency,
            updatedBaseSalary,
          );
        } else {
          state.averageSalary = 0;
        }
      }

      // calculate average allowance
      if (field === 'allowanceIncome' || field === 'allowanceFrequency') {
        const updatedAllowance =
          field === 'allowanceIncome' ? value : state.allowanceIncome;
        const updatedAllowanceFrequency =
          field === 'allowanceFrequency' ? value : state.allowanceFrequency;
        if (updatedAllowance && updatedAllowanceFrequency) {
          state.averageAllowance = getAverateAmount(
            updatedAllowanceFrequency,
            updatedAllowance,
          );
        } else {
          state.averageAllowance = 0;
        }
      }

      // calculate average commission
      if (field === 'commissionIncome' || field === 'commissionFrequency') {
        const updatedCommission =
          field === 'commissionIncome' ? value : state.commissionIncome;
        const updatedCommissionFrequency =
          field === 'commissionFrequency' ? value : state.commissionFrequency;
        if (updatedCommission && updatedCommissionFrequency) {
          state.averageCommission = getAverateAmount(
            updatedCommissionFrequency,
            updatedCommission,
          );
        } else {
          state.averageCommission = 0;
        }
      }

      // calculate coapplicant average salary
      if (
        field === 'coapplicantBaseSalary' ||
        field === 'coapplicantSalaryFrequency'
      ) {
        const updatedCoAppBaseSalary =
          field === 'coapplicantBaseSalary'
            ? value
            : state.coapplicantBaseSalary;
        const updatedCoAppSalaryFrequency =
          field === 'coapplicantSalaryFrequency'
            ? value
            : state.coapplicantSalaryFrequency;
        if (updatedCoAppBaseSalary && updatedCoAppSalaryFrequency) {
          state.coapplicantAverageSalary = getAverateAmount(
            updatedCoAppSalaryFrequency,
            updatedCoAppBaseSalary,
          );
        } else {
          state.coapplicantAverageSalary = 0;
        }
      }

      // calculate coapplicant average allowance
      if (
        field === 'coapplicantAllowanceIncome' ||
        field === 'coapplicantAllowanceFrequency'
      ) {
        const updatedCoAppAllowance =
          field === 'coapplicantAllowanceIncome'
            ? value
            : state.coapplicantAllowanceIncome;
        const updatedCoAppAllowanceFrequency =
          field === 'coapplicantAllowanceFrequency'
            ? value
            : state.coapplicantAllowanceFrequency;
        if (updatedCoAppAllowance && updatedCoAppAllowanceFrequency) {
          state.coapplicantAverageAllowance = getAverateAmount(
            updatedCoAppAllowanceFrequency,
            updatedCoAppAllowance,
          );
        } else {
          state.coapplicantAverageAllowance = 0;
        }
      }

      // calculate coapplicant average commission
      if (
        field === 'coapplicantCommissionIncome' ||
        field === 'coapplicantCommissionFrequency'
      ) {
        const updatedCoAppCommission =
          field === 'coapplicantCommissionIncome'
            ? value
            : state.coapplicantCommissionIncome;
        const updatedCoAppCommissionFrequency =
          field === 'coapplicantCommissionFrequency'
            ? value
            : state.coapplicantCommissionFrequency;
        if (updatedCoAppCommission && updatedCoAppCommissionFrequency) {
          state.coapplicantAverageCommission = getAverateAmount(
            updatedCoAppCommissionFrequency,
            updatedCoAppCommission,
          );
        } else {
          state.coapplicantAverageCommission = 0;
        }
      }

      // calculate stamp duty
      if (field === 'propertyLoaction' || field === 'maxPurchaseAmount') {
        const updatedPropertyLocation =
          field === 'propertyLoaction' ? value : state.propertyLoaction;
        const updatedMaxPurchasePrice =
          field === 'maxPurchaseAmount' ? value : state.maxPurchaseAmount;
        if (updatedMaxPurchasePrice) {
          state.stampDuty = getStampRate(
            updatedPropertyLocation,
            updatedMaxPurchasePrice,
          );
        } else {
          state.stampDuty = 0;
        }
      }

      // calculate average rent
      if (field === 'rentExpense' || field === 'frequencyOfRent') {
        const updatedRentExpenses =
          field === 'rentExpense' ? value : state.rentExpense;
        const updatedRentFrequency =
          field === 'frequencyOfRent' ? value : state.frequencyOfRent;
        if (updatedRentExpenses && updatedRentFrequency) {
          state.averageRent = getAverateAmount(
            updatedRentFrequency,
            updatedRentExpenses,
          );
        } else {
          state.averageRent = 0;
        }
      }

      // get rent currency
      if (field === 'residency') {
        const updatedResidency =
          field === 'residency' ? value : state.residency;
        if (updatedResidency) {
          const country = getCountryData(updatedResidency as TCountryCode);
          if (country && country.currency && country.currency.length > 0) {
            state.rentCurrency = country.currency[0];
          } else {
            state.rentCurrency = undefined;
          }
        }
      }
    },
    setMultipleNoteFormFields: (
      state,
      action: PayloadAction<Partial<NoteFormState>>,
    ) => {
      return { ...state, ...action.payload };
    },
    resetNoteForm: () => initialState,
  },
});

export const {
  setNoteFromField,
  setKidsAge,
  setPropertyPurposeMultiple,
  setMultipleNoteFormFields,
  resetNoteForm,
} = noteFormSlice.actions;
export default noteFormSlice.reducer;
